import React, { useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import HeroScene from "./three/HeroScene";
import Magnetic from "./Magnetic";
import { scrollToId } from "../lib/scroll";

const VARIANTS = [
    { id: "black", name: "Obsidian Black", swatch: "#181B26", ring: "#00F0FF" },
    { id: "silver", name: "Stellar Silver", swatch: "#DFE6F0", ring: "#38BDF8" },
    { id: "violet", name: "Cyber Violet", swatch: "#5B2BD6", ring: "#C084FC" },
];

const lineAnim = (i) => ({
    initial: { y: "115%" },
    animate: { y: "0%" },
    transition: {
        duration: 1.1,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.35 + i * 0.14,
    },
});

class WebGLBoundary extends React.Component {
    state = { failed: false };
    static getDerivedStateFromError() {
        return { failed: true };
    }
    render() {
        if (this.state.failed) {
            return (
                <div className="flex h-full w-full items-center justify-center">
                    <div className="h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(0,240,255,0.22),rgba(112,0,255,0.12)_45%,transparent_70%)]" />
                </div>
            );
        }
        return this.props.children;
    }
}

export default function Hero() {
    const [variant, setVariant] = useState("black");
    const [spinKey, setSpinKey] = useState(0);
    const active = VARIANTS.find((v) => v.id === variant);

    return (
        <section
            id="hero"
            className="relative h-[100svh] min-h-[640px] overflow-hidden"
        >
            {/* ambient glows behind the canvas */}
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
            >
                <div className="absolute -top-32 left-[8%] h-[34rem] w-[34rem] rounded-full bg-[radial-gradient(circle,rgba(0,240,255,0.13),transparent_65%)]" />
                <div className="absolute bottom-[-10rem] right-[4%] h-[36rem] w-[36rem] rounded-full bg-[radial-gradient(circle,rgba(112,0,255,0.16),transparent_65%)]" />
            </div>

            {/* 3D viewport — shifted right on desktop, upper half on mobile */}
            <div
                data-testid="nexora-hero-canvas-3d"
                className="absolute inset-x-0 top-0 h-[64%] md:inset-y-0 md:left-[20%] md:right-0 md:h-auto"
            >
                <WebGLBoundary>
                    <HeroScene variant={variant} spinKey={spinKey} />
                </WebGLBoundary>
            </div>

            {/* readability vignettes */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent md:via-ink/10"
            />

            {/* content */}
            <div className="pointer-events-none relative z-10 flex h-full flex-col justify-end px-6 pb-20 md:justify-center md:px-16 md:pb-0 lg:px-20">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="mb-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80 md:text-xs"
                >
                    <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-volt opacity-70" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-volt" />
                    </span>
                    Nexora One — Spatial Audio System
                </motion.p>

                <h1
                    data-testid="nexora-hero-title"
                    className="font-display text-[clamp(3.1rem,10.5vw,9rem)] font-extrabold uppercase leading-[0.9] tracking-[-0.02em] text-white"
                >
                    <span className="block overflow-hidden pb-1">
                        <motion.span className="block" {...lineAnim(0)}>
                            Sound
                        </motion.span>
                    </span>
                    <span className="block overflow-hidden pb-1">
                        <motion.span className="block" {...lineAnim(1)}>
                            Beyond
                        </motion.span>
                    </span>
                    <span className="block overflow-hidden pb-2">
                        <motion.span
                            className="block bg-gradient-to-r from-volt via-[#7C6CFF] to-hyper bg-clip-text text-transparent"
                            {...lineAnim(2)}
                        >
                            Reality.
                        </motion.span>
                    </span>
                </h1>

                <motion.p
                    data-testid="nexora-hero-subtitle"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.95 }}
                    className="mt-6 max-w-sm text-base font-light text-white/60 md:text-lg"
                >
                    Discover a new dimension of immersive audio.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.1 }}
                    className="pointer-events-auto mt-8 flex flex-wrap items-center gap-4"
                >
                    <Magnetic>
                        <button
                            data-testid="nexora-hero-explore-btn"
                            onClick={() => setSpinKey((k) => k + 1)}
                            className="group relative overflow-hidden rounded-full bg-volt px-7 py-3.5 text-sm font-semibold text-ink shadow-[0_0_32px_rgba(0,240,255,0.35)] transition-shadow duration-300 hover:shadow-[0_0_48px_rgba(0,240,255,0.6)]"
                        >
                            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                            Explore in 3D
                        </button>
                    </Magnetic>

                    <Magnetic>
                        <button
                            data-testid="nexora-hero-discover-btn"
                            onClick={() => scrollToId("technology")}
                            className="rounded-full border border-white/15 bg-white/5 px-7 py-3.5 text-sm font-medium text-white/85 backdrop-blur transition-all duration-300 hover:border-white/40 hover:bg-white/10"
                        >
                            Discover More
                        </button>
                    </Magnetic>

                    <div
                        className="ml-1 flex items-center gap-3 sm:ml-3"
                        role="group"
                        aria-label="Finish color selector"
                    >
                        {VARIANTS.map((v) => (
                            <button
                                key={v.id}
                                data-testid={`nexora-color-selector-${v.id}`}
                                aria-label={`Finish: ${v.name}`}
                                aria-pressed={variant === v.id}
                                onClick={() => setVariant(v.id)}
                                className="relative flex h-9 w-9 items-center justify-center rounded-full border transition-all duration-300"
                                style={{
                                    borderColor:
                                        variant === v.id ? v.ring : "rgba(255,255,255,0.18)",
                                    boxShadow:
                                        variant === v.id
                                            ? `0 0 18px ${v.ring}66`
                                            : "none",
                                }}
                            >
                                <span
                                    className="h-4 w-4 rounded-full transition-transform duration-300"
                                    style={{
                                        background: v.swatch,
                                        boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.25)`,
                                        transform:
                                            variant === v.id ? "scale(1.15)" : "scale(1)",
                                    }}
                                />
                            </button>
                        ))}
                        <span className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-white/45 sm:inline">
                            {active.name}
                        </span>
                    </div>
                </motion.div>
            </div>

            {/* spec micro-list — bottom right */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.3 }}
                className="pointer-events-none absolute bottom-10 right-16 z-10 hidden flex-col items-end gap-1.5 font-mono text-[10px] tracking-[0.25em] text-white/40 lg:flex"
            >
                <span>
                    <span className="text-volt/70">[DRIVER]</span> 50MM GRAPHENE
                </span>
                <span>
                    <span className="text-volt/70">[RESPONSE]</span> 4HZ – 48KHZ
                </span>
                <span>
                    <span className="text-volt/70">[ISOLATION]</span> −45DB
                </span>
            </motion.div>

            {/* drag hint */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.5 }}
                className="pointer-events-none absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-2 font-mono text-[10px] tracking-[0.35em] text-white/35 md:flex"
            >
                <motion.span
                    animate={{ x: [-5, 5, -5] }}
                    transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                >
                    <MoveHorizontal className="h-4 w-4" />
                </motion.span>
                DRAG TO ROTATE — 360°
            </motion.div>
        </section>
    );
}
